import { Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Spin } from "antd";
import { useSelector } from "react-redux";

import AppLayout from "./layouts/AppLayout";
import LoginPage from "./features/auth/LoginPage";
import { VARIANCE_ROUTES } from "./features/variance/routes";
import { NAV_ITEMS } from "./layouts/navigation";
import { selectAuthStatus } from "./app/authSlice";

/** Print sheets own their whole page — see the route group below. */
const isPrintRoute = (key) => key.startsWith("/print/");

/** Centred spinner — used both while auth resolves and while a lazy page loads. */
function Loader() {
  return (
    <div className="flex h-[60vh] items-center justify-center">
      <Spin size="large" />
    </div>
  );
}

/** Holds the route until the stored token has been checked, so a reload
 *  never bounces a signed-in user to /login. */
function RequireAuth({ children }) {
  const status = useSelector(selectAuthStatus);
  if (status === "unknown" || status === "loading") {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }
  return status === "authenticated" ? children : <Navigate to="/login" replace />;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Print sheets render OUTSIDE the app shell.
          They draw their own document — a sized page, its own header and
          footnote — and wrapping that in the sider, top bar and app footer
          made a sheet meant to be paper read as a page inside a page. Still
          behind RequireAuth: a plan is not public. */}
      <Route
        element={
          <RequireAuth>
            <Outlet />
          </RequireAuth>
        }
      >
        {NAV_ITEMS.filter(
          (item) => item.ready && item.element && isPrintRoute(item.key),
        ).map((item) => {
          const Page = item.element;
          return (
            <Route
              key={item.key}
              path={item.key}
              element={
                <Suspense fallback={<Loader />}>
                  <Page />
                </Suspense>
              }
            />
          );
        })}
      </Route>

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        {/* The pages come from navigation.jsx, so adding a screen means
            adding one entry there rather than editing three files. */}
        {NAV_ITEMS.filter(
          (item) => item.ready && item.element && !isPrintRoute(item.key),
        ).map((item) => {
          const Page = item.element;
          return (
            <Route
              key={item.key}
              path={item.key}
              element={
                <Suspense fallback={<Loader />}>
                  <Page />
                </Suspense>
              }
            />
          );
        })}
        {/* Variance is a staged workflow rather than a page: its stages and
            their subjects live in the URL, so it brings its own nested route
            tree instead of one NAV_ITEMS entry. */}
        {VARIANCE_ROUTES}

        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        {/* Optimization is part of the Planning Workbench: the advisor's surface
            renders there as a modal, which is the only place a recommendation
            can be applied. `?advisor=1` opens it, so an old link lands on the
            advisor rather than merely near it. */}
        <Route
          path="/advisor"
          element={<Navigate to="/planner?advisor=1" replace />}
        />
        {/* The Schedule Monitor merged into the Execution page as its
            "Plan adherence" tab — old links and the demo script land there. */}
        <Route
          path="/reconciliation"
          element={<Navigate to="/execution?tab=adherence" replace />}
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
